import React, { useState } from 'react';
import { 
  X, 
  Camera, 
  MapPin, 
  Upload, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Play,
  Video,
  Image as ImageIcon,
  HelpCircle
} from 'lucide-react';

interface Mission {
  id: string;
  title: string;
  description: string;
  points: number;
  verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
  quiz_questions?: QuizQuestion[];
}

interface QuizQuestion {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
}

interface MissionCompletionModalProps {
  mission: Mission;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: CompletionData) => Promise<void>;
}

interface CompletionData {
  proof_url?: string;
  location?: { lat: number; lng: number; address: string };
  quiz_answers?: number[];
  quiz_score?: number;
  notes?: string;
}

export default function MissionCompletionModal({ 
  mission, 
  isOpen, 
  onClose, 
  onComplete 
}: MissionCompletionModalProps) {
  const [step, setStep] = useState<'start' | 'upload' | 'location' | 'quiz' | 'notes' | 'complete'>('start');
  const [loading, setLoading] = useState(false);
  const [completionData, setCompletionData] = useState<CompletionData>({});
  
  // Photo/Video upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Location state
  const [locationEnabled, setLocationEnabled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number; address: string } | null>(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Quiz state
  const [quizAnswers, setQuizAnswers] = useState<number[]>([]);
  const [quizScore, setQuizScore] = useState<number | null>(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  
  // Notes state
  const [notes, setNotes] = useState('');

  // Reset state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setStep(getInitialStep());
      setCompletionData({});
      setSelectedFile(null);
      setPreviewUrl('');
      setUploadProgress(0);
      setLocationEnabled(false);
      setCurrentLocation(null);
      setQuizAnswers([]);
      setQuizScore(null);
      setQuizSubmitted(false);
      setNotes('');
      setLoading(false);
    }
  }, [isOpen, mission.verification_method]);

  const getInitialStep = () => {
    switch (mission.verification_method) {
      case 'photo': return 'upload';
      case 'location': return 'location';
      case 'quiz': return 'quiz';
      default: return 'start';
    }
  };

  const getNextStep = (currentStep: string) => {
    switch (currentStep) {
      case 'start': return 'notes';
      case 'upload': 
        if (mission.verification_method === 'photo') return 'notes';
        return 'location';
      case 'location': 
        return 'notes';
      case 'quiz': 
        return 'notes';
      case 'notes': 
        return 'complete';
      default: 
        return 'complete';
    }
  };

  if (!isOpen) return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(interval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // In a real app, you would upload to a cloud storage service
      // For demo purposes, we'll use the preview URL
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setUploadProgress(100);
      setCompletionData(prev => ({ ...prev, proof_url: previewUrl }));
      
      // Move to next step
      setStep(getNextStep('upload'));
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocationRequest = async () => {
    setLocationLoading(true);
    
    try {
      if (!navigator.geolocation) {
        throw new Error('Geolocation is not supported');
      }

      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        });
      });

      const { latitude, longitude } = position.coords;
      
      // In a real app, you would use a geocoding service to get the address
      const mockAddress = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
      
      const location = {
        lat: latitude,
        lng: longitude,
        address: mockAddress
      };

      setCurrentLocation(location);
      setLocationEnabled(true);
      setCompletionData(prev => ({ ...prev, location }));
    } catch (error) {
      console.error('Location request failed:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  const handleQuizSubmit = () => {
    const questions = getQuizQuestions();
    if (!questions.length) return;

    let correct = 0;
    questions.forEach((question, index) => {
      if (quizAnswers[index] === question.correct_answer) {
        correct++;
      }
    });

    const score = (correct / questions.length) * 100;
    setQuizScore(score);
    setQuizSubmitted(true);
    setCompletionData(prev => ({ 
      ...prev, 
      quiz_answers: quizAnswers,
      quiz_score: score
    }));
    
    // Move to next step after showing results
    setTimeout(() => {
      setStep(getNextStep('quiz'));
    }, 2000);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const finalData = {
        ...completionData,
        notes: notes.trim() || undefined
      };
      
      await onComplete(finalData);
      setStep('complete');
    } catch (error) {
      console.error('Mission completion failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getQuizQuestions = (): QuizQuestion[] => {
    // Return mission-specific questions if available, otherwise use defaults based on mission type
    if (mission.quiz_questions && mission.quiz_questions.length > 0) {
      return mission.quiz_questions;
    }

    // Default questions based on mission category/type
    const defaultQuestions: QuizQuestion[] = [
      {
        id: '1',
        question: `What is the main environmental benefit of "${mission.title}"?`,
        options: ['Reduces CO2 emissions', 'Saves water', 'Reduces waste', 'All of the above'],
        correct_answer: 3
      },
      {
        id: '2',
        question: 'How can you make this eco-action part of your daily routine?',
        options: ['Do it once and forget', 'Only when convenient', 'Make it a regular habit', 'Only on weekends'],
        correct_answer: 2
      },
      {
        id: '3',
        question: 'What impact does this action have on the environment?',
        options: ['No real impact', 'Small positive impact', 'Significant positive impact', 'Negative impact'],
        correct_answer: 2
      }
    ];

    return defaultQuestions;
  };

  const renderStartStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="h-8 w-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Complete Your Mission?</h3>
        <p className="text-gray-600">
          {mission.description}
        </p>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-center space-x-2 text-emerald-700 mb-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
          <span className="font-medium">Reward: {mission.points} EcoCoins</span>
        </div>
        <p className="text-sm text-emerald-600">
          Complete this mission to earn EcoCoins and contribute to your environmental impact!
        </p>
      </div>

      <button
        onClick={() => setStep(getNextStep('start'))}
        className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
      >
        <Play className="h-5 w-5" />
        <span>Start Mission</span>
      </button>
    </div>
  );

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Share Your Action</h3>
        <p className="text-gray-600">
          Upload a photo showing your completed mission
        </p>
      </div>

      {!selectedFile ? (
        <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-emerald-400 transition-colors">
          <div className="space-y-4">
            <div className="flex justify-center space-x-4">
              <div className="bg-emerald-100 p-3 rounded-lg">
                <Camera className="h-8 w-8 text-emerald-600" />
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Video className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            
            <div>
              <p className="text-lg font-medium text-gray-900 mb-2">
                Take a photo or record a video
              </p>
              <p className="text-sm text-gray-500 mb-4">
                PNG, JPG, MP4 up to 10MB
              </p>
              
              <label className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium cursor-pointer transition-colors inline-flex items-center space-x-2">
                <Upload className="h-5 w-5" />
                <span>Choose File</span>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* File Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center space-x-4">
              <div className="bg-emerald-100 p-2 rounded-lg">
                {selectedFile.type.startsWith('video/') ? (
                  <Video className="h-6 w-6 text-emerald-600" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-emerald-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={() => {
                  setSelectedFile(null);
                  setPreviewUrl('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {previewUrl && (
              <div className="mt-4">
                {selectedFile.type.startsWith('video/') ? (
                  <video
                    src={previewUrl}
                    controls
                    className="w-full h-48 object-cover rounded-lg"
                  />
                ) : (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                )}
              </div>
            )}
          </div>

          {/* Upload Progress */}
          {loading && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Uploading...</span>
                <span className="text-gray-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleFileUpload}
            disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="h-5 w-5" />
                <span>Upload & Continue</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  const renderLocationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Share Your Location</h3>
        <p className="text-gray-600">
          Help us verify your mission by sharing your current location
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <div className="flex items-start space-x-3">
          <MapPin className="h-6 w-6 text-blue-600 mt-1" />
          <div className="flex-1">
            <h4 className="font-medium text-blue-900 mb-2">Location Verification</h4>
            <p className="text-sm text-blue-700 mb-4">
              This helps us confirm you completed the mission at the right place and contributes to our impact mapping.
            </p>
            
            {currentLocation ? (
              <div className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-gray-900">Location Captured</span>
                </div>
                <p className="text-sm text-gray-600">{currentLocation.address}</p>
              </div>
            ) : (
              <button
                onClick={handleLocationRequest}
                disabled={locationLoading}
                className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                {locationLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Getting Location...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>Share Location</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex space-x-3">
        <button
          onClick={() => setStep(getNextStep('location'))}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 py-3 rounded-lg font-medium transition-colors"
        >
          Skip Location
        </button>
        <button
          onClick={() => setStep(getNextStep('location'))}
          disabled={!locationEnabled}
          className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );

  const renderQuizStep = () => {
    const questions = getQuizQuestions();

    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="h-8 w-8 text-blue-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Knowledge Check</h3>
          <p className="text-gray-600">
            Answer these questions to show your understanding of the environmental impact
          </p>
        </div>

        {quizSubmitted && quizScore !== null ? (
          <div className="text-center space-y-4">
            <div className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full ${
              quizScore >= 70 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              {quizScore >= 70 ? (
                <CheckCircle className="h-6 w-6" />
              ) : (
                <AlertCircle className="h-6 w-6" />
              )}
              <span className="font-medium text-lg">
                Score: {quizScore.toFixed(0)}% ({questions.filter((_, i) => quizAnswers[i] === questions[i].correct_answer).length}/{questions.length} correct)
              </span>
            </div>
            <p className="text-gray-600">
              {quizScore >= 70 
                ? 'Excellent! You understand the environmental impact of this action.' 
                : 'Good effort! Every action counts toward a greener future.'
              }
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <p className="text-emerald-700 font-medium">
                Moving to the next step...
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {questions.map((question, questionIndex) => (
              <div key={question.id} className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-medium text-gray-900 mb-4">
                  {questionIndex + 1}. {question.question}
                </h4>
                <div className="space-y-3">
                  {question.options.map((option, optionIndex) => (
                    <label
                      key={optionIndex}
                      className={`flex items-center space-x-3 p-4 rounded-lg cursor-pointer transition-colors ${
                        quizAnswers[questionIndex] === optionIndex
                          ? 'bg-emerald-100 border-2 border-emerald-300'
                          : 'bg-white border-2 border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`question-${questionIndex}`}
                        value={optionIndex}
                        checked={quizAnswers[questionIndex] === optionIndex}
                        onChange={() => {
                          const newAnswers = [...quizAnswers];
                          newAnswers[questionIndex] = optionIndex;
                          setQuizAnswers(newAnswers);
                        }}
                        className="text-emerald-500 focus:ring-emerald-500 w-4 h-4"
                      />
                      <span className="text-gray-700 flex-1">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <button
              onClick={handleQuizSubmit}
              disabled={quizAnswers.length !== questions.length || loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-5 w-5" />
                  <span>Submit Quiz ({quizAnswers.length}/{questions.length})</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderNotesStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Add Your Thoughts</h3>
        <p className="text-gray-600">
          Share your experience or any additional details about completing this mission
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes (Optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="How did this mission make you feel? Any challenges or insights you'd like to share?"
          className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
          rows={4}
        />
      </div>

      <button
        onClick={handleComplete}
        disabled={loading}
        className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Completing Mission...</span>
          </>
        ) : (
          <>
            <CheckCircle className="h-5 w-5" />
            <span>Complete Mission</span>
          </>
        )}
      </button>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="text-center space-y-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="h-10 w-10 text-green-600" />
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Mission Completed! 🎉</h3>
        <p className="text-gray-600 mb-4">
          Congratulations on completing "{mission.title}"
        </p>
        
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
          <div className="flex items-center justify-center space-x-2 text-emerald-700 mb-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            <span className="font-bold text-xl">+{mission.points} EcoCoins Earned!</span>
          </div>
          {completionData.quiz_score !== undefined && (
            <p className="text-emerald-600 text-sm">
              Quiz Score: {completionData.quiz_score.toFixed(0)}%
            </p>
          )}
        </div>
      </div>

      <button
        onClick={onClose}
        className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-medium transition-colors"
      >
        Continue Your Journey
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{mission.title}</h2>
            <p className="text-sm text-gray-600">Complete your eco-mission</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'start' && renderStartStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'location' && renderLocationStep()}
          {step === 'quiz' && renderQuizStep()}
          {step === 'notes' && renderNotesStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
}